import * as React from 'react';

import type { ProductLookupRow } from '@graphql/queries/PerceptionModelQueries';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import GgvarStrip from './GgvarStrip';
import type { Candidate } from './normSetUtils';
import ProductDetailPanel from './ProductDetailPanel';

/**
 * The review table. One row per candidate, ranked nearest first, with the
 * anchor mean pinned at the top as the reference the strips are read against.
 */

type Props = {
  candidates: Candidate[];
  lookupByName: Map<string, ProductLookupRow>;
  anchorMean: Record<string, number>;
  attributes: string[];
  included: Set<string>;
  onToggle: (product: string) => void;
  onToggleAll: (checked: boolean) => void;
};

const CandidateTable: React.FC<Props> = ({
  candidates,
  lookupByName,
  anchorMean,
  attributes,
  included,
  onToggle,
  onToggleAll,
}) => {
  const [expanded, setExpanded] = React.useState<string | null>(null);

  // Counted over the ranked rows only: this control sits in the candidate
  // table, so it must not silently drop products picked off the map.
  const rankedIncluded = candidates.filter((c) =>
    included.has(c.product),
  ).length;
  const allIncluded =
    candidates.length > 0 && rankedIncluded === candidates.length;

  // One colour scale for the whole table, so cell intensity is comparable
  // between rows rather than being renormalised per product.
  const maxAbsDelta = React.useMemo(() => {
    let max = 0;
    candidates.forEach((c) => {
      const g = lookupByName.get(c.product)?.ggvars;
      attributes.forEach((attr) => {
        const v = g?.[attr]?.intensity;
        const ref = anchorMean[attr];
        if (Number.isFinite(v) && Number.isFinite(ref)) {
          max = Math.max(max, Math.abs((v as number) - ref));
        }
      });
    });
    return max || 1;
  }, [candidates, lookupByName, anchorMean, attributes]);

  return (
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              checked={allIncluded}
              indeterminate={rankedIncluded > 0 && !allIncluded}
              onChange={(e) => onToggleAll(e.target.checked)}
              inputProps={{
                'aria-label': allIncluded
                  ? 'Exclude all ranked candidates'
                  : 'Include all ranked candidates',
              }}
            />
          </TableCell>
          <TableCell width={40}>#</TableCell>
          <TableCell>Product</TableCell>
          <TableCell align="right">Mean dist</TableCell>
          <TableCell align="right">Anchors</TableCell>
          <TableCell>
            Flavor profile vs anchors
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              orange = above · blue = below · hatched = no data
            </Typography>
          </TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {/* Reference row — what every strip below is measured against. */}
        <TableRow sx={{ backgroundColor: 'rgba(58,124,165,0.06)' }}>
          <TableCell padding="checkbox" />
          <TableCell />
          <TableCell>
            <strong>Anchor mean</strong>
          </TableCell>
          <TableCell align="right">—</TableCell>
          <TableCell align="right">—</TableCell>
          <TableCell>
            <GgvarStrip
              values={anchorMean}
              reference={anchorMean}
              attributes={attributes}
              maxAbsDelta={maxAbsDelta}
            />
          </TableCell>
        </TableRow>

        {candidates.map((c) => {
          const row = lookupByName.get(c.product);
          const isOpen = expanded === c.product;
          const values: Record<string, number> = {};
          attributes.forEach((a) => {
            const v = row?.ggvars?.[a]?.intensity;
            if (Number.isFinite(v)) values[a] = v as number;
          });

          return (
            <React.Fragment key={c.product}>
              <TableRow hover selected={included.has(c.product)}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={included.has(c.product)}
                    onChange={() => onToggle(c.product)}
                    inputProps={{
                      'aria-label': `Include ${c.product} in norm set`,
                    }}
                  />
                </TableCell>
                <TableCell>{c.rank}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => setExpanded(isOpen ? null : c.product)}
                    aria-label={isOpen ? 'Hide detail' : 'Show detail'}
                  >
                    {isOpen ? (
                      <KeyboardArrowDown fontSize="small" />
                    ) : (
                      <KeyboardArrowRight fontSize="small" />
                    )}
                  </IconButton>
                  {c.product}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {c.meanDist.toFixed(3)}
                </TableCell>
                <TableCell align="right">
                  <Tooltip
                    title={
                      c.censored
                        ? 'Outside some anchors\u2019 nearest-peer lists; distance is a lower bound for those anchors'
                        : 'Listed by every anchor'
                    }
                    arrow
                  >
                    <span>
                      {c.anchorHits}
                      {c.censored ? '*' : ''}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <GgvarStrip
                    values={values}
                    reference={anchorMean}
                    attributes={attributes}
                    maxAbsDelta={maxAbsDelta}
                  />
                </TableCell>
              </TableRow>

              {isOpen && row && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                    <Box sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                      <ProductDetailPanel
                        row={row}
                        anchorMean={anchorMean}
                        attributes={attributes}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default CandidateTable;
